import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import {
  IDeleteDataResponseAndPayload,
  TResponse,
  TResponseWithQData,
} from "../../../types/common";
import {
  IPurchaseOrder,
  IPOCreatePayload,
  IPOUpdatePayload,
  IPOStatusPayload,
  IPOCancelPayload,
} from "../../../types/api/purchase-order";
import {
  listPO,
  poDetail,
  createPO,
  updatePO,
  deletePO,
  generatePOShareLink,
  updatePOStatus,
  cancelPO,
} from "./callbacks";

export const usePOList = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<TResponseWithQData<IPurchaseOrder[]>, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<TResponseWithQData<IPurchaseOrder[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IPurchaseOrder[]>, Error>({
    queryKey: [API_ENDPOINTS.PO_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await listPO(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map((x) => ({ key: x.id, ...x })),
      qData: { ...queryParams, total: res.extra?.total },
    }),
    initialData: {
      data: [],
      success: true,
      message: "",
      code: 200,
      qData: { page: 1, limit: 20 },
      extra: { page: 1, limit: 20, total: 0 },
    },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
            setQueryParams(params as QData);
    },
  };
};

export const usePODetail = (
  id: string | number,
  options?: Omit<
    UseQueryOptions<TResponse<IPurchaseOrder>, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<TResponse<IPurchaseOrder>, Error> => {
  return useQuery<TResponse<IPurchaseOrder>, Error>({
    queryKey: [API_ENDPOINTS.PO_DETAIL, id],
    queryFn: async () => {
      const res = await poDetail(id);
      return res.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    ...options,
  });
};

export const usePOStats = (
  options?: Omit<
    UseQueryOptions<TResponse<Record<string, number>>, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<TResponse<Record<string, number>>, Error> => {
  return useQuery<TResponse<Record<string, number>>, Error>({
    queryKey: [API_ENDPOINTS.PO_STATS],
    queryFn: async () => {
      const { default: axiosLib } = await import("../../../lib/axios");
      const res = await axiosLib.get(API_ENDPOINTS.PO_STATS);
      return res.data;
    },
    staleTime: 30000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const usePOCreate = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IPurchaseOrder>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IPurchaseOrder>,
    unknown,
    IPOCreatePayload
  > = {},
): UseMutationResult<TResponse<IPurchaseOrder>, unknown, IPOCreatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IPurchaseOrder>, unknown, IPOCreatePayload>({
    mutationFn: async (data) => {
      const res = await createPO(data);
      return res.data;
    },
    onSuccess: (res, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.PO_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;
          return {
            ...old,
            data: [res.data, ...old.data],
            extra: { ...old.extra, total: (old.extra?.total ?? 0) + 1 },
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.PO_STATS] });
      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const usePOUpdate = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IPurchaseOrder>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IPurchaseOrder>,
    unknown,
    IPOUpdatePayload
  > = {},
): UseMutationResult<TResponse<IPurchaseOrder>, unknown, IPOUpdatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IPurchaseOrder>, unknown, IPOUpdatePayload>({
    mutationFn: async (data) => {
      const res = await updatePO(data);
      return res.data;
    },
    onSuccess: (res, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.PO_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;
          return {
            ...old,
            data: old.data.map((po: IPurchaseOrder) =>
              po.id === res.data?.id ? res.data : po,
            ),
          };
        },
      );
      queryClient.setQueryData([API_ENDPOINTS.PO_DETAIL, res.data?.id], res);
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.PO_STATS] });
      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const usePODelete = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IDeleteDataResponseAndPayload>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IDeleteDataResponseAndPayload>,
    unknown,
    IDeleteDataResponseAndPayload
  > = {},
): UseMutationResult<
  TResponse<IDeleteDataResponseAndPayload>,
  unknown,
  IDeleteDataResponseAndPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<
    TResponse<IDeleteDataResponseAndPayload>,
    unknown,
    IDeleteDataResponseAndPayload
  >({
    mutationFn: async (data) => {
      const res = await deletePO(data);
      return res.data;
    },
    onSuccess: (res, vars, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.PO_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;
          const deletedId = Number(vars.ids);
          return {
            ...old,
            data: old.data.filter((po: IPurchaseOrder) => po.id !== deletedId),
            extra: {
              ...old.extra,
              total: Math.max((old.extra?.total ?? 1) - 1, 0),
            },
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.PO_STATS] });
      onSuccess?.(res);
      options?.onSuccess?.(res, vars, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const usePOUpdateStatus = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IPurchaseOrder>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IPurchaseOrder>,
    unknown,
    IPOStatusPayload
  > = {},
): UseMutationResult<TResponse<IPurchaseOrder>, unknown, IPOStatusPayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IPurchaseOrder>, unknown, IPOStatusPayload>({
    mutationFn: async (data) => {
      const res = await updatePOStatus(data);
      return res.data;
    },
    onSuccess: (res, vars, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.PO_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;
          return {
            ...old,
            data: old.data.map((po: IPurchaseOrder) =>
              po.id === res.data?.id ? res.data : po,
            ),
          };
        },
      );
      queryClient.setQueryData([API_ENDPOINTS.PO_DETAIL, vars.id], res);
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.PO_STATS] });
      onSuccess?.(res);
      options?.onSuccess?.(res, vars, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const usePOCancel = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IPurchaseOrder>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<IPurchaseOrder>,
    unknown,
    IPOCancelPayload
  > = {},
): UseMutationResult<TResponse<IPurchaseOrder>, unknown, IPOCancelPayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IPurchaseOrder>, unknown, IPOCancelPayload>({
    mutationFn: async (data) => {
      const res = await cancelPO(data);
      return res.data;
    },
    onSuccess: (res, vars, ...rest) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.PO_LIST, { ...qData, total: null }],
        (old: any) => {
          if (!old) return;
          return {
            ...old,
            data: old.data.map((po: IPurchaseOrder) =>
              po.id === res.data?.id ? res.data : po,
            ),
          };
        },
      );
      queryClient.setQueryData([API_ENDPOINTS.PO_DETAIL, vars.id], res);
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.PO_STATS] });
      onSuccess?.(res);
      options?.onSuccess?.(res, vars, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

// ── Generate Share Link ───────────────────────────────────────────────────────

export const usePOGenerateShareLink = (
  options?: UseMutationOptions<TResponse<{ token: string }>, Error, number> & {
    onSuccess?: (token: string) => void;
    onError?: (e: any) => void;
  },
): UseMutationResult<TResponse<{ token: string }>, Error, number> => {
  return useMutation<TResponse<{ token: string }>, Error, number>({
    mutationFn: async (id) => (await generatePOShareLink(id)).data,
    onSuccess: (res) => options?.onSuccess?.(res.data?.token ?? ""),
    onError: options?.onError,
  });
};
